import React, { useState, useEffect } from 'react';
import { Check, Calculator } from 'lucide-react';

interface MathGameProps {
  onComplete: (score: number) => void;
}

function makeQuestion(level: number) {
  const maxNum = 10 * level; // Increases max number by 10 every level

  const op = Math.random() > 0.5 ? '+' : '-';
  // Ensure positive result for subtraction
  let a = Math.floor(Math.random() * maxNum) + 1;
  let b = Math.floor(Math.random() * maxNum) + 1;

  if (op === '-' && b > a) [a, b] = [b, a];

  return { text: `${a} ${op} ${b}`, answer: op === '+' ? a + b : a - b };
}

export default function MathGame({ onComplete }: MathGameProps) {
  const [question, setQuestion] = useState('');
  const [answer, setAnswer] = useState(0);
  const [userAnswer, setUserAnswer] = useState('');
  const [score, setScore] = useState(0);
  const [timeLeft, setTimeLeft] = useState(60); // More time, 60s
  const [isActive, setIsActive] = useState(false);
  const [wrongAnswer, setWrongAnswer] = useState(false);

  // Difficulty multiplier
  const [level, setLevel] = useState(1);

  useEffect(() => {
    const q = makeQuestion(1);
    setQuestion(q.text);
    setAnswer(q.answer);
  }, []);

  useEffect(() => {
    if (!isActive) return;
    
    // Stable timer - runs independently of other state changes
    const timer = setInterval(() => {
      setTimeLeft((t) => Math.max(0, t - 1));
    }, 1000);
    
    return () => clearInterval(timer);
  }, [isActive]);

  // Game over check
  useEffect(() => {
    if (timeLeft === 0 && isActive) {
      setIsActive(false);
      onComplete(score);
    }
  }, [timeLeft, isActive, onComplete, score]);

  const startGame = () => {
      setScore(0);
      setTimeLeft(60);
      setLevel(1);
      setIsActive(true);
      generateQuestion(1);
  };

  const generateQuestion = (currentLevel = level) => {
    const q = makeQuestion(currentLevel);
    setQuestion(q.text);
    setAnswer(q.answer);
    setUserAnswer('');
    setWrongAnswer(false);
  };

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    if (parseInt(userAnswer) === answer) {
      const newScore = score + 10;
      setScore(newScore);
      const newLevel = level + 1;
      setLevel(newLevel);
      generateQuestion(newLevel);
    } else {
        setWrongAnswer(true);
        setUserAnswer(''); // clear on wrong
    }
  };

  return (
    <div className="flex flex-col items-center justify-center p-4 text-foreground w-full max-w-md mx-auto h-full">
      <div className="flex items-center gap-2 mb-6">
        <Calculator size={32} className="text-green-400" />
        <h2 className="text-2xl font-bold">חשבון מהיר</h2>
      </div>

      {!isActive ? (
          <div className="text-center space-y-8">
              <p className="text-muted-foreground text-lg">
                  פתרו כמה שיותר תרגילים ב-60 שניות!
                  <br />
                  כל תשובה נכונה מעלה את הרמה.
              </p>
              <button 
                  onClick={startGame}
                  className="bg-green-600 hover:bg-green-500 text-white px-12 py-4 rounded-full font-bold text-xl shadow-lg transition-transform hover:scale-105"
              >
                  התחל משחק
              </button>
          </div>
      ) : (
          <>
            <div className="flex justify-between w-full px-4 mb-4 text-muted-foreground font-mono">
                <span>רמה: {level}</span>
                <span>זמן: {timeLeft}s</span>
            </div>

            <div dir="ltr" className="text-4xl font-bold bg-muted px-12 py-6 rounded-2xl mb-8 border border-border font-mono">
                {question} = ?
            </div>

            <form onSubmit={handleSubmit} className="flex flex-col items-center gap-4 w-full">
                <div className="flex items-center gap-2 justify-center w-full">
                    <input 
                    type="number" 
                    value={userAnswer}
                    onChange={(e) => {
                        setUserAnswer(e.target.value);
                        setWrongAnswer(false);
                    }}
                    className={`bg-muted text-center text-3xl p-4 rounded-xl focus:outline-none focus:ring-2 w-32 font-mono transition-all ${wrongAnswer ? 'focus:ring-red-500 border-2 border-red-500' : 'focus:ring-green-500'}`}
                    placeholder="?"
                    autoFocus
                    />
                    <button type="submit" className="bg-green-600 p-4 rounded-xl font-bold hover:bg-green-500 transition-colors">
                    <Check size={32} />
                    </button>
                </div>
                {wrongAnswer && (
                    <p className="text-red-400 font-bold bg-red-400/10 px-4 py-2 rounded-lg animate-shake">
                        תשובה שגויה, נסה שוב!
                    </p>
                )}
            </form>

            <div className="mt-8 text-xl font-bold text-yellow-400">
                ניקוד: {score}
            </div>
          </>
      )}
    </div>
  );
}
